import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ComponentStore } from '@ngrx/component-store';
import { tap, withLatestFrom } from 'rxjs';
import { NetworkStatsStore } from './network-stats.store';
import { OfflineStatusComponent } from './offline-status.component';

interface InterceptorState {
	isOffline: boolean;
	showingNotification: boolean;
	dialogRef?: MatDialogRef<unknown>;
}

@Injectable({
	providedIn: 'root',
})
export class RetryInterceptorStore extends ComponentStore<InterceptorState> {
	readonly isOffline$ = this.select((state) => state.isOffline);
	readonly isShowingNotification = this.select((state) => state.showingNotification);
	readonly dialogRef$ = this.select((state) => state.dialogRef);

	/**
	 * Records that there's a network error - that the network is unavailable/offline.
	 * If not already showing a notification, then show one.
	 */
	readonly networkError = this.effect((trigger$) => {
		return trigger$.pipe(
			withLatestFrom(this.isShowingNotification),
			tap(([, showingNotification]) => {
				this.patchState({
					isOffline: true,
				});

				if (!showingNotification) {
					this.statsStore.bumpOfflineTimes();
					this.showNotification();
				}
			}),
		);
	});

	/**
	 * Records that the network is OK - that the network is available/online.
	 * If there's a notification showing, it is hidden.
	 */
	readonly networkOk = this.effect((trigger$) => {
		return trigger$.pipe(
			withLatestFrom(this.isShowingNotification),
			tap(([, showingNotification]) => {
				this.patchState({
					isOffline: false,
				});

				if (showingNotification) {
					this.hideNotification();
				}
			}),
		);
	});

	private readonly showNotification = this.effect((trigger$) => {
		return trigger$.pipe(
			tap(() => {
				const dialogRef = this.dialog.open(OfflineStatusComponent, {
					closeOnNavigation: false,
					disableClose: true,
					hasBackdrop: true,
					maxWidth: '60ch',
					position: {
						top: '64px',
						left: '50vw',
					},
					panelClass: 'translate-x-50pct',
				});

				this.patchState({
					showingNotification: true,
					dialogRef,
				});
			}),
		);
	});

	private readonly hideNotification = this.effect((trigger$) => {
		return trigger$.pipe(
			withLatestFrom(this.dialogRef$),
			tap(([, dialogRef]) => {
				this.patchState({
					showingNotification: false,
					dialogRef: undefined,
				});

				dialogRef?.close();
			}),
		);
	});

	constructor(
		private dialog: MatDialog,
		private statsStore: NetworkStatsStore,
	) {
		super({ isOffline: false, showingNotification: false });
	}
}
